import AppComponent from '../base/app-component.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import { enforcePerm } from '../lib/perm.ts';
import { getCasseMap, type CassaInfo } from '../lib/casse-info.ts';
import { buildCessatoInfo, type PosizioneRaw } from '../lib/cessato-info.ts';

type ModalInstance = { show(): void; hide(): void; dispose(): void };
type TabName = 'deleghe' | 'disdette' | 'storico' | 'altri-settori';

function hasPosizioniInCasse(posizioni: Record<string, PosizioneRaw> | undefined, casse: CassaInfo[]): boolean {
  if (!posizioni) return false;
  return casse.some(c => Boolean(posizioni[c.codice]));
}

export class DettaglioLavoratore extends AppComponent {
  static override componentName = 'wc-dettaglio-lavoratore';

  protected override get templateId() { return 'dettaglio-lavoratore'; }

  private lavoratoreId = 0;
  private loadedTabs = new Set<TabName>();
  private delegheLavoratore: Record<string, unknown>[] = [];
  private modals: ModalInstance[] = [];

  protected override afterRender(): void {
    this.lavoratoreId = this.attributeNumber('lavoratore-id');
    if (!this.lavoratoreId) {
      this.showError('ID lavoratore non specificato');
      return;
    }
    this.setupDelegheActions();
    this.loadDettaglio();
  }

  // --- Azioni tab deleghe (setup una volta in afterRender) ---

  private setupDelegheActions(): void {
    this.delegate.on('click', '[data-action="nuova-delega"]', () => {
      this.resetFormNuovaDelega();
      this.getModal('#modalNuovaDelega')?.show();
    });

    this.delegate.on('click', '[data-action="dettagli-delega"]', async (e: Event) => {
      e.preventDefault();
      const el = (e.target as HTMLElement).closest('[data-action="dettagli-delega"]') as HTMLElement;
      const id = Number(el?.dataset.id);
      if (id) this.showDettaglioDelega(id);
    });

    this.delegate.on('click', '[data-action="modifica-delega"]', (e: Event) => {
      e.preventDefault();
      const el = (e.target as HTMLElement).closest('[data-action="modifica-delega"]') as HTMLElement;
      const id = Number(el?.dataset.id);
      if (id) this.handleModificaDelega(id);
    });

    this.delegate.on('click', '[data-action="imposta-bozza"]', (e: Event) => {
      e.preventDefault();
      const el = (e.target as HTMLElement).closest('[data-action="imposta-bozza"]') as HTMLElement;
      const id = Number(el?.dataset.id);
      if (id) this.handleCambiaStatoDelega(id, 'BOZZA');
    });

    this.delegate.on('click', '[data-action="imposta-da-presentare"]', (e: Event) => {
      e.preventDefault();
      const el = (e.target as HTMLElement).closest('[data-action="imposta-da-presentare"]') as HTMLElement;
      const id = Number(el?.dataset.id);
      if (id) this.handleCambiaStatoDelega(id, 'DA_PRESENTARE');
    });

    this.delegate.on('click', '[data-action="cancella-delega"]', (e: Event) => {
      e.preventDefault();
      const el = (e.target as HTMLElement).closest('[data-action="cancella-delega"]') as HTMLElement;
      const id = Number(el?.dataset.id);
      if (id) this.handleCancellaDelega(id);
    });

    this.listen(document, 'submit', (e: Event) => {
      const form = e.target as HTMLFormElement;
      if (form.matches('[data-form="nuova-delega"]')) {
        e.preventDefault();
        this.handleSubmitNuovaDelega();
      }
    });

    this.listen(document, 'change', (e: Event) => {
      const input = e.target as HTMLInputElement;
      if (input.name === 'nd-cassa' && input.closest('#modalNuovaDelega')) {
        this.checkCassaWarning(input.value);
      }
    });
  }

  // --- Dettaglio e tab ---

  private async loadDettaglio(): Promise<void> {
    const result = await this.api.lavoratori.dettaglio(this.lavoratoreId);
    const container = this.$('[data-content]');
    if (!container) return;

    if (!result.ok) {
      if (result.status === 404) {
        container.innerHTML =
          '<div class="text-center py-4">' +
          '<p class="text-muted mb-3">Lavoratore non trovato</p>' +
          '<a href="/lavoratori" class="btn btn-outline-secondary btn-sm">' +
          '<i class="bi bi-arrow-left me-1"></i>Torna alla ricerca</a></div>';
      } else {
        this.showError(result.error);
      }
      return;
    }

    const data = result.data as Record<string, unknown>;
    const casse = Object.values(getCasseMap());
    const posizioni = data.posizioni as Record<string, PosizioneRaw> | undefined;
    templateParseToElement('dettaglio-lavoratore-scheda', container, {
      ...data,
      casse,
      cessato: buildCessatoInfo(posizioni),
      hasPosizioni: hasPosizioniInCasse(posizioni, casse),
    });

    this.setupTabListeners();
    this.loadTab('deleghe');
  }

  private setupTabListeners(): void {
    const tabContainer = this.$('.nav-tabs');
    if (!tabContainer) return;

    this.listen(tabContainer, 'shown.bs.tab', (e: Event) => {
      const target = e.target as HTMLElement;
      const tabName = target.dataset.tab as TabName | undefined;
      if (tabName && !this.loadedTabs.has(tabName)) {
        this.loadTab(tabName);
      }
    });
  }

  private async loadTab(tabName: TabName): Promise<void> {
    const container = this.$(`[data-tab-content="${tabName}"]`);
    if (!container) return;

    templateParseToElement('shared-loading-state', container, {});

    const loaders: Record<TabName, () => Promise<void>> = {
      deleghe: () => this.loadDeleghe(container),
      disdette: () => this.loadDisdette(container),
      storico: () => this.loadStorico(container),
      'altri-settori': () => this.loadAltriSettori(container),
    };

    await loaders[tabName]();
    this.loadedTabs.add(tabName);
  }

  private async loadDeleghe(container: HTMLElement): Promise<void> {
    const result = await this.api.lavoratori.deleghe(this.lavoratoreId);
    if (!result.ok) {
      templateParseToElement('shared-error-state', container, { message: result.error });
      return;
    }
    const deleghe = result.data.deleghe;
    this.delegheLavoratore = deleghe.map(d => ({ ...d } as Record<string, unknown>));
    templateParseToElement('dettaglio-lavoratore-deleghe', container, {
      deleghe,
      casse: Object.values(getCasseMap()),
    });
    enforcePerm(container);
    this.appendDelegheModals();
  }

  private async loadDisdette(container: HTMLElement): Promise<void> {
    const result = await this.api.lavoratori.disdette(this.lavoratoreId);
    if (!result.ok) {
      templateParseToElement('shared-error-state', container, { message: result.error });
      return;
    }
    templateParseToElement('dettaglio-lavoratore-disdette', container, { disdette: result.data.disdette });
  }

  private async loadStorico(container: HTMLElement): Promise<void> {
    const result = await this.api.lavoratori.storico(this.lavoratoreId);
    if (!result.ok) {
      templateParseToElement('shared-error-state', container, { message: result.error });
      return;
    }
    templateParseToElement('dettaglio-lavoratore-storico', container, { storico: result.data.storico });
  }

  private async loadAltriSettori(container: HTMLElement): Promise<void> {
    const result = await this.api.lavoratori.iscrizioniAltriSettori(this.lavoratoreId);
    if (!result.ok) {
      templateParseToElement('shared-error-state', container, { message: result.error });
      return;
    }
    templateParseToElement('dettaglio-lavoratore-altri-settori', container, {
      iscrizioni: result.data.iscrizioni,
      empty: result.data.iscrizioni.length === 0,
    });
  }

  // --- Modale Bootstrap ---

  private appendDelegheModals(): void {
    for (const modal of this.$$('[data-modal]')) {
      if (!modal.id) continue;
      const existing = document.getElementById(modal.id);
      if (existing && existing !== modal) existing.remove();
      document.body.appendChild(modal);
    }
  }

  private getModal(selector: string): ModalInstance | null {
    const el = document.querySelector(selector);
    if (!el) return null;
    const bs = (globalThis as any).bootstrap;
    const existing = bs?.Modal?.getInstance(el);
    if (existing) return existing;
    const m = new bs.Modal(el);
    this.modals.push(m);
    return m;
  }

  private resetFormNuovaDelega(): void {
    const root = document.getElementById('modalNuovaDelega');
    if (!root) return;
    for (const r of root.querySelectorAll('input[name="nd-cassa"]') as NodeListOf<HTMLInputElement>) {
      r.checked = false;
    }
    const opSelect = root.querySelector('[data-field="operatore"]') as HTMLSelectElement | null;
    if (opSelect) opSelect.value = '';
    const dateField = root.querySelector('[data-field="dataDecorrenza"]') as HTMLInputElement | null;
    if (dateField) dateField.value = '';
    const noteField = root.querySelector('[data-field="note"]') as HTMLTextAreaElement | null;
    if (noteField) noteField.value = '';
    const warning = root.querySelector('[data-cassa-warning]') as HTMLElement | null;
    if (warning) warning.classList.add('d-none');
    const errEl = root.querySelector('[data-validation-error]') as HTMLElement | null;
    if (errEl) { errEl.textContent = ''; errEl.classList.add('d-none'); }
    const submitBtn = root.querySelector('button[type="submit"]') as HTMLButtonElement | null;
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.innerHTML = '<i class="bi bi-check-circle me-1"></i>Crea delega';
    }
  }

  // --- Warning cassa duplicata ---

  private checkCassaWarning(cassa: string): void {
    const root = document.getElementById('modalNuovaDelega');
    if (!root) return;
    const warning = root.querySelector('[data-cassa-warning]') as HTMLElement | null;
    const submitBtn = root.querySelector('button[type="submit"]') as HTMLButtonElement | null;
    if (!warning) return;
    const dup = this.delegheLavoratore.find(d => d.cassa === cassa && d.stato !== 'ARCHIVIATA');
    if (dup) {
      const stato = String(dup.stato ?? '');
      warning.textContent = `Attenzione: esiste già una delega ${cassa} (${this.statoLabel(stato)})`;
      warning.classList.remove('d-none');
      if (submitBtn) submitBtn.disabled = true;
    } else {
      warning.classList.add('d-none');
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  private statoLabel(stato: string): string {
    return stato.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
  }

  // --- Submit nuova delega ---

  private async handleSubmitNuovaDelega(): Promise<void> {
    const root = document.getElementById('modalNuovaDelega');
    if (!root) return;

    const cassa = (root.querySelector('input[name="nd-cassa"]:checked') as HTMLInputElement | null)?.value;
    const operatore = (root.querySelector('[data-field="operatore"]') as HTMLSelectElement | null)?.value;
    const dataDecorrenza = (root.querySelector('[data-field="dataDecorrenza"]') as HTMLInputElement | null)?.value || undefined;
    const note = (root.querySelector('[data-field="note"]') as HTMLTextAreaElement | null)?.value || undefined;

    if (!cassa) {
      this.mostraValidationError(root, 'Seleziona la cassa');
      return;
    }
    if (!operatore) {
      this.mostraValidationError(root, 'Seleziona l\'operatore');
      return;
    }

    const submitBtn = root.querySelector('button[type="submit"]') as HTMLButtonElement | null;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Salvataggio...';
    }

    const result = await this.api.deleghe.crea({
      lavoratoreId: this.lavoratoreId,
      cassa,
      dataDecorrenza,
      operatore,
      note,
    });

    if (!result.ok) {
      this.mostraValidationError(root, result.error);
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = '<i class="bi bi-check-circle me-1"></i>Crea delega';
      }
      return;
    }

    this.getModal('#modalNuovaDelega')?.hide();
    Weext.toast('Delega creata', { type: 'success' });
    this.reloadDelegheTab();
  }

  // --- Dettaglio delega (modale informativa) ---

  private async showDettaglioDelega(id: number): Promise<void> {
    const root = document.getElementById('modalDettaglioDelega');
    if (!root) return;
    const content = root.querySelector('[data-delega-content]') as HTMLElement;
    if (!content) return;

    const delega = this.delegheLavoratore.find(d => Number(d.id) === id);
    if (!delega) return;

    templateParseToElement('shared-dettaglio-delega', content, {
      ...delega,
      operatoreNome: delega.operatoreNome ?? null,
    });
    this.getModal('#modalDettaglioDelega')?.show();
  }

  // --- Azioni riga delega ---

  private async handleModificaDelega(id: number): Promise<void> {
    const result = await Weext.modalIframe(`/deleghe/${id}/modifica?layout=modal`, {
      title: 'Modifica delega',
      size: 'lg',
    });
    if (result.refresh) this.reloadDelegheTab();
  }

  private async handleCambiaStatoDelega(id: number, nuovoStato: 'BOZZA' | 'DA_PRESENTARE'): Promise<void> {
    const result = await this.api.deleghe.cambiaStato(id, nuovoStato);
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
      return;
    }
    const msg = nuovoStato === 'BOZZA' ? 'Delega impostata come bozza' : 'Delega pronta per la presentazione';
    Weext.toast(msg, { type: 'success' });
    this.reloadDelegheTab();
  }

  private async handleCancellaDelega(id: number): Promise<void> {
    const ok = await Weext.confirm('Vuoi cancellare questa delega?', {
      title: 'Conferma cancellazione',
      confirmText: 'Cancella',
      confirmClass: 'btn-danger',
    });
    if (!ok) return;

    const result = await this.api.deleghe.elimina(id);
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
      return;
    }
    Weext.toast('Delega cancellata', { type: 'success' });
    this.reloadDelegheTab();
  }

  // --- Reload tab deleghe ---

  private reloadDelegheTab(): void {
    this.loadedTabs.delete('deleghe');
    this.loadTab('deleghe');
  }

  // --- Helpers ---

  private mostraValidationError(root: Element, messaggio: string): void {
    const el = root.querySelector('[data-validation-error]');
    if (!el) return;
    el.textContent = messaggio;
    el.classList.remove('d-none');
  }

  private showError(message: string): void {
    const container = this.$('[data-content]');
    if (container) {
      templateParseToElement('shared-error-state', container, { message });
    }
  }

  // --- Cleanup ---

  protected override onDisconnected(): void {
    for (const m of this.modals) {
      try { m.dispose(); } catch { /* ignore */ }
    }
    this.modals = [];
    document.getElementById('modalNuovaDelega')?.remove();
    document.getElementById('modalDettaglioDelega')?.remove();
  }
}
